import {
  BeneficialOwnerControlType,
  useInheritableUltimateBeneficialOwners,
} from '@commenda/api';
import { Checkbox, Loading } from '@commenda/kakashi';
import { getCountryName } from '@commenda/utils';
import { useEffect, useMemo } from 'react';
import { ControlTypePicker } from './ControlTypePicker';

export type InheritedUboSelection = {
  directorId: number;
  controlType: BeneficialOwnerControlType;
};

interface InheritableUbosSectionProps {
  corporationId: number;
  parentCorporationId: number;
  parentEntityName?: string;
  // null = "use defaults" — section materialises to all-checked with the
  // default control type the moment the parent's UBO list loads.
  // Non-null (incl. []) is an explicit user pick.
  selections: InheritedUboSelection[] | null;
  onSelectionsChange: (next: InheritedUboSelection[]) => void;
}

const DEFAULT_INHERITED_CONTROL_TYPE =
  BeneficialOwnerControlType.INDIRECT_OWNERSHIP;

export const InheritableUltimateBeneficialOwnersSection = ({
  corporationId,
  parentCorporationId,
  parentEntityName,
  selections,
  onSelectionsChange,
}: InheritableUbosSectionProps) => {
  const { data, isLoading } = useInheritableUltimateBeneficialOwners(
    corporationId,
    parentCorporationId,
  );

  const byDirectorId = useMemo(() => {
    const m = new Map<number, BeneficialOwnerControlType>();
    for (const s of selections ?? []) m.set(s.directorId, s.controlType);
    return m;
  }, [selections]);

  // selections === null means "no user pick yet" — seed all UBOs with the
  // default control type once the parent's list arrives. From this point
  // on the form-state drives the checkboxes; the section never auto-seeds
  // again. The parent form is responsible for resetting back to null when
  // the trigger condition (linked-entity change, HSC off→on cycle) fires.
  useEffect(() => {
    if (selections === null && data && data.length > 0) {
      onSelectionsChange(
        data.map((u) => ({
          directorId: u.directorId,
          controlType: DEFAULT_INHERITED_CONTROL_TYPE,
        })),
      );
    }
  }, [selections, data, onSelectionsChange]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-md">
        <Loading />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col gap-xs rounded-md border-[0.5px] border-border-primary p-xl">
        <p className="txt-small-medium text-text-primary">Inherited UBOs</p>
        <p className="txt-extra-small-regular text-text-tertiary">
          {parentEntityName
            ? `${parentEntityName} has no individual UBOs of its own. You can still mark this entity as a significant controller.`
            : 'This entity has no individual UBOs of its own. You can still mark this entity as a significant controller.'}
        </p>
      </div>
    );
  }

  const selectedSet = new Set(
    (selections ?? data.map((u) => ({ directorId: u.directorId }))).map(
      (s) => s.directorId,
    ),
  );

  const toggle = (directorId: number) => {
    const current = selections ?? [];
    const exists = current.some((s) => s.directorId === directorId);
    if (exists) {
      onSelectionsChange(current.filter((s) => s.directorId !== directorId));
    } else {
      onSelectionsChange([
        ...current,
        { directorId, controlType: DEFAULT_INHERITED_CONTROL_TYPE },
      ]);
    }
  };

  const setControlType = (
    directorId: number,
    controlType: BeneficialOwnerControlType,
  ) => {
    const current = selections ?? [];
    onSelectionsChange(
      current.map((s) =>
        s.directorId === directorId ? { ...s, controlType } : s,
      ),
    );
  };

  return (
    <div className="flex flex-col gap-md rounded-md border-[0.5px] border-border-primary p-xl">
      <div className="flex flex-col gap-xxs">
        <p className="txt-small-medium text-text-primary">
          Ultimate Beneficial Owners
        </p>
        <p className="txt-extra-small-regular text-text-tertiary">
          Select which of the source entity&apos;s UBOs to also record on
          this corporation.
        </p>
      </div>
      <div className="flex flex-col gap-md">
        {data.map((ubo) => {
          const isSelected = selectedSet.has(ubo.directorId);
          const controlType =
            byDirectorId.get(ubo.directorId) ??
            DEFAULT_INHERITED_CONTROL_TYPE;
          const countryName = getCountryName(ubo.nationality) ?? null;

          return (
            <div
              key={ubo.directorId}
              className="flex flex-col gap-sm rounded-sm border-[0.5px] border-border-primary bg-bg-primary p-md"
            >
              <div className="flex items-start gap-sm">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggle(ubo.directorId)}
                />
                <div className="flex flex-col gap-xxs">
                  <span className="txt-small-bold text-text-primary">
                    {ubo.name}
                  </span>
                  {countryName && (
                    <span className="txt-extra-small-regular text-text-secondary">
                      {countryName}
                    </span>
                  )}
                </div>
              </div>
              {isSelected && (
                <div className="flex flex-col gap-xs pl-7">
                  <span className="txt-extra-small-medium text-text-secondary">
                    UBO control type
                  </span>
                  <ControlTypePicker
                    value={controlType}
                    onChange={(v) => setControlType(ubo.directorId, v)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
